import { useLayoutEffect, useRef } from 'react';
import {
  prepareTicketEmailHtmlForDisplay,
  scopeTicketEmailStyles,
} from '../../lib/ticketEmailHtml';

const SHADOW_BASE_CSS = `
:host {
  display: block;
  font-size: 11pt;
  line-height: 1.15;
  overflow-wrap: anywhere;
  color: #1a2332;
  max-width: 100%;
  height: auto !important;
  max-height: none !important;
  overflow: visible !important;
}
:host(.ticket-mail__body--quoted) {
  opacity: 0.88;
  border-left: 3px solid #e5e7eb;
  padding-left: 10px;
  margin-top: 6px;
}
p { margin: 0; line-height: 1.15; }
table {
  border-collapse: collapse;
  max-width: 100% !important;
  width: auto;
  table-layout: auto;
}
td, th {
  word-break: break-word;
  overflow-wrap: anywhere;
}
img {
  max-width: 100%;
  height: auto;
}
a { color: #1565c0; }
`;

/** Last so email &lt;style&gt; cannot force a nested scroll area on the host. */
const SHADOW_SCROLL_RESET_CSS = `
:host {
  height: auto !important;
  max-height: none !important;
  min-height: 0 !important;
  overflow: visible !important;
}
.ticket-mail__email-root {
  height: auto !important;
  max-height: none !important;
  overflow: visible !important;
}
`;

/**
 * Render email HTML inside an open Shadow DOM so &lt;style&gt; / body rules
 * cannot change the rest of the CRM page (font-size, rem scale, etc.).
 */
export function IsolatedEmailHtml({
  className,
  html,
}: {
  className?: string;
  html: string;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const lastHtmlRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const prepared = scopeTicketEmailStyles(
      prepareTicketEmailHtmlForDisplay(html),
      ':host',
    );
    if (lastHtmlRef.current === prepared) return;
    lastHtmlRef.current = prepared;

    const shadow = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
    shadow.innerHTML =
      `<style>${SHADOW_BASE_CSS}</style>` +
      `<div class="ticket-mail__email-root">${prepared}</div>` +
      `<style>${SHADOW_SCROLL_RESET_CSS}</style>`;
  }, [html]);

  return <div ref={hostRef} className={className} />;
}
